import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { PostEditorContext } from '../states/PostEditorProvider';
import { UseFormReturn } from 'react-hook-form';
import { Toggle } from '../../../components/forms/Toggle';
import { CustomizeSchedulePerProfile } from '../CustomizeSchedulePerProfile';
import { CustomizeSchedulePerProfileControllerProps } from '../CustomizeSchedulePerProfile/CustomizeSchedulePerProfile.controller';
import { ScheduleBox } from './ScheduleBox';
import { PostEditorGroupDataContext } from '../states/PostEditorGroupDataProvider';
import { mapSelectedProfilesValues } from '../helpers/mapSelectedProfilesValues';
import { SelectedSocialProfileValue } from '../../../components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import classNames from 'classnames';
import classes from './Schedule.module.scss';
import { Tooltip } from '../../../components/actions/Tooltip';
import { AppContext } from '../../../providers/AppProvider';
import { PostEditorContextEnum } from '../../../models/post-editor';
import { useCustomizePerProfileWarning } from '../../../hooks/useCustomizePerProfileWarning';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { BeaconContext } from '../../../providers/BeaconProvider';

export type ScheduleViewProps = {
  //
  formInstance: UseFormReturn;
  postId?: string;
  hideCustomizeToggle: boolean;
};

export const ScheduleView = ({ formInstance, postId, hideCustomizeToggle }: ScheduleViewProps): JSX.Element => {
  const { beacon } = useContext(BeaconContext);
  const { postEditorContext } = useContext(AppContext);
  const { state, dispatch, profiles: availableProfiles } = useContext(PostEditorContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const { postGroups } = state;
  const postGroup = postGroups[postGroupIndex];
  const { customizeSchedulePerProfile, activeCustomizeIndex, profiles } = postGroup;
  const stringifiedProfiles = JSON.stringify(profiles, undefined, '');
  const selectedProfiles = useMemo(() => {
    const parsedProfiles: SelectedSocialProfileValue[] = JSON.parse(stringifiedProfiles);
    return mapSelectedProfilesValues(parsedProfiles, availableProfiles);
  }, [stringifiedProfiles, availableProfiles]);
  const { toggleAction, warningModalElement } = useCustomizePerProfileWarning('scheduling');

  const customizeIndex =
    typeof activeCustomizeIndex === 'number' && customizeSchedulePerProfile ? activeCustomizeIndex : undefined;

  const onTabChange: CustomizeSchedulePerProfileControllerProps['onTabChange'] = useCallback(
    (customizeIndex: number | null): void => {
      dispatch({ type: 'setActiveCustomizeIndex', payload: { customizeIndex, postGroupIndex } });
    },
    [postGroupIndex, dispatch]
  );

  const [enableCustomizeSchedule, setEnableCustomizeSchedule] = useState<boolean>(selectedProfiles.length > 1);

  useEffect(() => {
    setEnableCustomizeSchedule(selectedProfiles.length > 1);
  }, [selectedProfiles]);

  return (
    <>
      {[PostEditorContextEnum.duplicate, PostEditorContextEnum.new, PostEditorContextEnum.import].includes(
        postEditorContext.type
      ) && !hideCustomizeToggle ? (
        <div
          className={classNames(
            'margin-bottom-small-16 margin-bottom-tablet-portrait-24 grid-x align-justify align-middle',
            classes.Schedule
          )}>
          <div className={'grid-x align-middle'}>
            <div className={'cell shrink'}>
              <Toggle
                checked={customizeSchedulePerProfile}
                disabled={state.loading || !enableCustomizeSchedule}
                formInstance={formInstance}
                name={'customizeSchedulePerProfile'}
                rules={{}}
                action={toggleAction}
                label={'Customize per Profile'}
              />
            </div>
            {!state.loading ? (
              <div className={'cell shrink'}>
                <Tooltip
                  target={`#tooltipTargetSchedule_${
                    customizeIndex !== undefined ? customizeIndex : 'group'
                  }_${postGroupIndex}`}
                  position={'right'}
                  pointer
                  type={'inlined'}
                  autoZIndex={false}
                  autoHide={false}
                  style={{ zIndex: scssVars['z-index-overmodal'] }}>
                  <div>
                    When publishing to multiple Profiles, you can customize the timing for each Profile.{' '}
                    <a onClick={() => beacon('article', '667', { type: 'modal' })}>Learn more</a>.
                  </div>
                </Tooltip>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {customizeSchedulePerProfile ? (
        <div className={'expand-margins'}>
          <CustomizeSchedulePerProfile
            {...(postId ? { postId } : {})}
            formInstance={formInstance}
            onTabChange={onTabChange}
            activeIndex={activeCustomizeIndex}
          />
        </div>
      ) : (
        <ScheduleBox formInstance={formInstance} />
      )}
      {warningModalElement}
    </>
  );
};
